import { useState } from 'react';
import { isAxiosError } from 'axios';
import { Alert, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, TextField, Typography } from '@mui/material';
import { getAnioEnCurso } from '../../utils/Numeracion';
import { CODIGO_CONFIRMAR_INTERCAMBIO, CODIGO_REQUIERE_SERIE, ICambioTitularOpciones } from '../../types/EntidadFacturacion';

export type ModoCambioTitular = 'intercambio' | 'serie';

interface RespuestaError { message?: string; codigo?: string }

export const mensajeDeError = (e: unknown): string => {
  const data = isAxiosError<RespuestaError>(e) ? e.response?.data : undefined;
  return data?.message || (e instanceof Error ? e.message : 'Error al guardar los datos');
};

// Si el guardado necesita que el usuario confirme el intercambio o indique la serie nueva, el modo del diálogo y el
// mensaje del servidor; si es cualquier otro error, null.
export const pideCambioTitular = (e: unknown): { modo: ModoCambioTitular; mensaje: string } | null => {
  const data = isAxiosError<RespuestaError>(e) && e.response?.status === 409 ? e.response.data : undefined;
  if (data?.codigo === CODIGO_CONFIRMAR_INTERCAMBIO) return { modo: 'intercambio', mensaje: data.message ?? '' };
  if (data?.codigo === CODIGO_REQUIERE_SERIE) return { modo: 'serie', mensaje: data.message ?? '' };
  return null;
};

interface CambioTitularDialogProps {
  open: boolean;
  onClose: () => void;
  modo: ModoCambioTitular;
  mensaje: string;      // el del servidor: nombra las entidades afectadas
  pais?: string;        // país de la entidad: el año de la serie es el de su zona horaria
  // Reintenta el guardado con la confirmación o la serie nueva; si falla, el diálogo muestra el error y sigue abierto.
  onConfirmar: (opciones: ICambioTitularOpciones) => Promise<void>;
}

// Cambio de NIF de una entidad que ya factura (cambio de titular). Para Hacienda el emisor es el NIF, así que la
// identidad anterior nunca se edita en su sitio:
//   - 'intercambio': el NIF nuevo es el de una secundaria → pasa a ser la principal y cada una conserva sus facturas y
//     su numeración. Solo se confirma.
//   - 'serie': NIF nuevo → la identidad anterior queda en una entidad desactivada con sus facturas y su numeración, y
//     esta empieza una serie nueva. Como al crear una entidad, no se precarga nada: la serie es una decisión consciente.
const CambioTitularDialog = ({ open, onClose, modo, mensaje, pais, onConfirmar }: CambioTitularDialogProps) => {
  const currentYear = getAnioEnCurso(pais);

  const [seriePrefix, setSeriePrefix] = useState('');
  const [counter, setCounter] = useState(0);
  const [serieError, setSerieError] = useState<string | undefined>(undefined);
  const [counterError, setCounterError] = useState<string | undefined>(undefined);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const formatInvoiceNumber = () => `${seriePrefix}${currentYear}-${(counter + 1).toString().padStart(6, "0")}`;

  const validateSerie = () => {
    let valid = true;

    if (!seriePrefix || seriePrefix.trim() === '') {
      setSerieError('La serie es obligatoria');
      valid = false;
    } else if (!/^[A-Z0-9]+$/.test(seriePrefix)) {
      setSerieError('La serie solo admite letras y números');
      valid = false;
    } else if (seriePrefix.length > 6) {
      setSerieError('El prefijo de la serie admite hasta 6 caracteres');
      valid = false;
    } else {
      setSerieError(undefined);
    }

    if (!Number.isInteger(counter) || counter < 0 || counter > 999999) {
      setCounterError('El número inicial debe estar entre 0 y 999999');
      valid = false;
    } else {
      setCounterError(undefined);
    }

    return valid;
  };

  const handleConfirmar = async () => {
    if (modo === 'serie' && !validateSerie()) return;

    setLoading(true);
    setError(null);
    try {
      await onConfirmar(modo === 'intercambio'
        ? { confirmarIntercambio: true }
        : { numeracion: { serie: `${seriePrefix}${currentYear}`, counter } });
    } catch (err: unknown) {
      setError(mensajeDeError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={loading ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 'bold' }}>
        {modo === 'intercambio' ? 'Cambio de entidad principal' : 'Cambio de NIF: numeración nueva'}
      </DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        <Typography variant="body2" sx={{ mb: 2 }}>{mensaje}</Typography>

        {modo === 'intercambio' ? (
          <Typography variant="body2">
            La entidad principal es la que tiene la suscripción a Zyra. Las facturas ya emitidas no cambian: cada una
            sigue en la entidad de su titular, con su número y sus datos.
          </Typography>
        ) : (
          <>
            <Typography variant="body2" sx={{ mb: 2 }}>
              Las facturas ya emitidas no cambian y seguirán disponibles en esa entidad desactivada. Para rectificarlas
              o emitir sus borradores, tendrás que activarla. Define la serie con la que este NIF empezará a facturar:
              debe tener un prefijo que no use ninguna otra entidad.
            </Typography>
            <TextField
              label="Serie (prefijo)*"
              fullWidth
              disabled={loading}
              value={seriePrefix}
              onChange={(e) => setSeriePrefix(e.target.value.toUpperCase())}
              error={!!serieError}
              helperText={serieError}
            />
            <TextField
              label="Número inicial*"
              type="number"
              fullWidth
              disabled={loading}
              value={counter}
              onChange={(e) => setCounter(Number(e.target.value))}
              error={!!counterError}
              helperText={counterError}
              sx={{ mt: 2 }}
            />
            <Typography variant="body1" sx={{ mt: 2 }}>
              Próxima factura a emitir: <b>{formatInvoiceNumber()}</b>
            </Typography>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button disabled={loading} onClick={onClose}>Cancelar</Button>
        <Button disabled={loading} variant="contained" onClick={handleConfirmar}>
          {loading ? <CircularProgress size={20} /> : 'Confirmar'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CambioTitularDialog;
